/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, ViewChild} from '@angular/core';
import {BaseComponent, FileService, ImagePreviewComponent, ImagePreviewPicture, Picture, Product, SortingPosition} from 'compob2b-libs';
import {FileUploader} from 'ng2-file-upload';
import {from, Observable, Subscription} from 'rxjs';
import {catchError, concatMap, finalize, map, take, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';
import {CrudEvent} from '../../interfaces/crud-event';

interface ImageFile {
  file: File;
}

@Component({
  selector: 'app-additional-pictures',
  templateUrl: './additional-pictures.component.html',
  styleUrls: ['./additional-pictures.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdditionalPicturesComponent extends BaseComponent implements OnChanges {
  @Input() model: Product;
  @Input() uri: string;
  @Input() dangerText: string;
  @Input() maxFileSize: number;
  @Input() allowedMimeType: string[];
  @Output() removeAdditionalPictureEvent = new EventEmitter<Picture>(true);
  @Output() loadingEvent = new EventEmitter<CrudEvent>();
  @Output() pictureEvent = new EventEmitter<CrudEvent>();
  @Output() pictureChangeEvent = new EventEmitter<CrudEvent>();
  @ViewChild(ImagePreviewComponent) preview: ImagePreviewComponent;

  uploader: FileUploader;
  hasDropZoneOver: boolean;
  showUploadForm = false;
  private subscription: Subscription;
  toEmit = false;

  constructor(protected readonly fileService: FileService
  ) {
    super();
  }

  private initUploader() {
    this.uploader = new FileUploader({
      url: null,
      disableMultipart: false,
      formatDataFunctionIsAsync: false,
      isHTML5: true,
      allowedFileType: ['image'],
      removeAfterUpload: true,
      autoUpload: false,
      maxFileSize: this.maxFileSize ?? 5 * 1024 * 1024,
      allowedMimeType: this.allowedMimeType ?? ['image/png', 'image/jpg', 'image/jpeg', 'image/gif']
    });
    this.hasDropZoneOver = false;
  }

  public upload(): void {
    if (this.uploader.queue.length) {
      this.loadingEvent.emit(CrudEvent.UPLOADING);
      this.subscription = from(this.uploader.queue)
        .pipe(
          map((uploadingFile) => uploadingFile._file),
          concatMap((uploadingFile, index) => this.uploadFile(uploadingFile, index)),
          finalize(() => this.finishUpload()),
          takeUntil(this.unsubscribe),
        ).subscribe(resp => {
          this.successLoadingPicture(resp);

        }, error => this.handleError(error));
      this.hasDropZoneOver = false;
    }

  }

  showUpdateMessage() {
    this.successToast(translations.updated);
  }

  private finishUpload(): void {
    this.loadingEvent.emit(CrudEvent.UPLOADING_DONE);
    this.uploader.clearQueue();
    this.showUpdateMessage();
    this.subscription.unsubscribe();
  }

  private uploadFile(uploadingFile: File, index: number): Observable<any> {
    if (this.model?.pictures.length === 0 && index === 0) {
      this.toEmit = true;
      return this.compoApiService.postForm<ImageFile, string>(this.uri + '/upload-main-picture', {file: uploadingFile});
    }
    return this.compoApiService.postForm<ImageFile, string>(this.uri + '/upload-picture', {file: uploadingFile});
  }

  protected successLoadingPicture(res) {
    if (this.model.pictures.findIndex(item => item.id === res.data.id) === -1) {
      this.model.pictures.push(res.data);
    }
    if (!this.model?.picture && this.toEmit === true) {
      this.pictureEvent.emit(CrudEvent.UPDATE_PICTURE);
    }
  }

  public fileOver(e: any): void {
    this.hasDropZoneOver = e;
  }

  ngOnChanges(): void {
    if (this.model) {
      this.initUploader();
    }
  }

  drop(event: CdkDragDrop<any>) {
    const prevIndex = event.previousContainer.data.index;
    const targetIndex = event.container.data.index;

    this.widgetsMove(this.model.pictures, prevIndex, targetIndex);

    this.model.pictures.forEach((value, idx) => value.pos = idx);
    this.updatePicturesPositions();
  }

  widgetsMove(arr: any, fromIndex: number, toIndex: number) {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
    return arr;
  }

  updatePicturesPositions() {
    const uri = this.uri + '/pictures';
    const sortingPositions: SortingPosition[] = [];
    this.model.pictures.forEach(picture => sortingPositions.push({id: picture.id, pos: picture.pos}));
    this.compoApiService.post(uri, sortingPositions).pipe(take(1), catchError(err => this.handleError(err))).subscribe();
  }

  removeAdditionalPicture(picture: Picture) {
    this.confirm(translations.confirmDelete + ' ' + translations.image + '?', new Observable(() => {
      this.removePicture(picture);
    }));
  }

  removePicture(picture: Picture) {
    const uri = this.uri + '/picture/' + picture.id;
    this.compoApiService.delete<Picture>(uri)
      .pipe(
        tap(_ => this.removePictureFromModel(picture)),
        take(1),
        catchError(err => this.handleError(err))
      ).subscribe(_ => {
      this.showUpdateMessage();
    });
  }

  protected removePictureFromModel(picture: Picture) {
    const index = this.model.pictures.findIndex(el => el.id === picture.id);
    this.model.pictures.splice(index, 1);
    this.cdr.detectChanges();
  }

  openPicture(index: number) {
    this.preview.show(index, this.model.pictures
      .map(item => {
        return {
          src: this.fileService.getImageUri(item),
          picture: item
        } as ImagePreviewPicture;
      }));
  }

  getImageUri(picture: Picture) {
    return this.fileService.getImageUri(picture, 200) ?? this.noPhotoSrc;
  }

  handleImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.className += ' error';
    console.log(img.className);
    img.onerror = null; // чтобы избежать бесконечного цикла если дефолтное тоже не загрузится
  }

}
